// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { TelemetryEvent } from '@imprivata-cloud/eamc-chatbot';

/**
 * Stable mapping from upstream TelemetryEvent.type to Pendo event names
 * .
 */
const NAME_BY_TYPE: Record<TelemetryEvent['type'], string> = {
  chat_panel_opened: 'eamc_chatbot_panel_opened',
  chat_panel_closed: 'eamc_chatbot_panel_closed',
  question_submitted: 'eamc_chatbot_question_submitted',
  answer_rendered: 'eamc_chatbot_answer_rendered',
  feedback_submitted: 'eamc_chatbot_feedback_submitted',
  pipeline_error: 'eamc_chatbot_pipeline_error',
};

/**
 * Forward each upstream TelemetryEvent to `window.pendo.track`.
 *
 * Contract enforced:
 * - One Pendo event per upstream event variant; names follow the eamc_chatbot_* namespace.
 * - Only non-discriminant fields from the upstream event are forwarded as properties.
 *   No host-supplied identity data (tenantId, userId, roleType) is added — that
 *   context lives on the Pendo visitor/account from the existing `pendo.initialize()`
 *   call.
 * - Silent drop (no throw, no queue) when `window.pendo` or `pendo.track` is absent.
 *   Pendo loads async and may be absent in local-dev or on first paint.
 */
export const forwardTelemetryToPendo = (event: TelemetryEvent): void => {
  const pendo = (globalThis as { pendo?: { track?: (name: string, props?: object) => void } })
    .pendo;
  if (!pendo?.track) {
    return; // silent drop — Pendo not loaded
  }

  const { type, ...properties } = event; // strip discriminant; forward all other fields
  pendo.track(NAME_BY_TYPE[type], properties);
};
